import { Button, Flex, Grid, Image, Spinner, Text, useToast } from '@chakra-ui/react'
import type { VinOnlineVehicleDto } from 'BrokerTsLib/src/dtos'
import { getVehicleInformationItems } from 'BrokerTsLib/src/dtos'
import { useState } from 'react'

import { useGetBrandLogo } from '@/hooks/useDocuments'
import car from '@/images/apps/car.svg'

const CarDetailInfo = ({
  label,
  value,
}: {
  label: string
  value?: string | number | undefined
}) => {
  return (
    <Grid sx={{ gap: 0 }}>
      <Text>{label}</Text>
      {value && <Text sx={{ fontWeight: 'bold' }}>{value}</Text>}
    </Grid>
  )
}

type CarDetailsProps = {
  vehicle: VinOnlineVehicleDto
  onInsertToForm?: () => Promise<any>,
  showInsertButton?: boolean
}

export const CarDetails = ({
  vehicle,
  onInsertToForm,
  showInsertButton = true
}: CarDetailsProps) => {
  const toast = useToast()
  const [isInserting, setIsInserting] = useState(false)
  const p = 6
  const bgColor = 'neutral.50'
  const borderSideRadius = 'sm'

  const handleInsertToForm = () => {
    setIsInserting(true)

    onInsertToForm?.()
      .then(() => {
        toast({
          title: 'Údaje o vozidle byly vloženy do formuláře',
          status: 'success',
          duration: 3000,
          isClosable: true,
        })
      })
      .catch((err) => {
        toast({
          title: 'Nepodařilo se získat informace o vozidle',
          description: 'Doplňte údaje o vozidle ručně podle technického průkazu.',
          status: 'error',
          duration: 3000,
          isClosable: true,
        })
      })
      .finally(() => setIsInserting(false))
  }

  const info = getVehicleInformationItems(vehicle)
    // nezobrazuji údaje, které jsou již nějakým způsobem v nadpisech (model, vin...)
    .filter(x => ![vehicle.vin, vehicle.registrationMark, vehicle.Generace, vehicle.druh, vehicle.MakeBase, vehicle.Verze, vehicle.modelMerged].includes(x.value as any))

  const { data, isLoading, isError } = useGetBrandLogo(vehicle.CategoryID, vehicle.MakeBase, { retry: false })

  return (
    <Flex sx={{ w: '100%', gap: 1, flexWrap: { base: 'wrap', xl: 'nowrap' } }}>
      <Grid
        sx={{
          minW: {
            base: '100%',
            xl: '210px',
          },
          p,
          bgColor,
          justifyItems: 'center',
          borderLeftRadius: borderSideRadius,
        }}
      >
        {isLoading && <Spinner />}
        {!isLoading && isError && <Image src={car} />}
        {!isLoading && !isError && data?.data &&
          <Image
            src={'data:image/png;base64,' + data?.data}
            alt={vehicle.MakeBase}
            sx={{ w: 36 }}
          />
        }
        <Text
          sx={{
            fontSize: 'lg',
            minH: '1.5rem',
            textAlign: 'center',
            textTransform: 'uppercase',
            fontWeight: 'bold'
          }}
        >
          {vehicle.vin || ' '}
        </Text>

        <Text
          sx={{
            fontSize: 'md',
            minH: '1.5rem',
            textAlign: 'center',
            textTransform: 'uppercase',
          }}
        >
          {vehicle.registrationMark || ' '}
        </Text>
      </Grid>

      <Grid
        sx={{
          w: '100%',
          gap: 6,
          p,
          bgColor,
          borderRightRadius: borderSideRadius,
        }}
      >
        <Flex
          sx={{
            alignContent: 'center',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: 4,
          }}
        >
          <Grid sx={{ alignContent: 'start' }}>
            <Text sx={{ color: 'primary.500', fontWeight: 'bold' }}>
              {vehicle.MakeBase} {vehicle.ModelText}, {vehicle.Verze}
            </Text>

            <Text sx={{ color: 'primary.500' }}>
              {vehicle.druh}{vehicle.druh && <>, </>}{vehicle.Generace}{vehicle.Generace && <>. generace</>}
            </Text>
          </Grid>

          {showInsertButton &&
            <Button
              colorScheme="primary"
              isLoading={isInserting}
              onClick={handleInsertToForm}
            >
              Vložit do formuláře
            </Button>
          }
        </Flex>

        <Grid
          sx={{
            gridTemplateColumns: {
              base: '1fr 1fr',
              md: '1fr 1fr 1fr',
            },
            gap: 3,
          }}
        >
          {info &&
            info.length > 0 &&
            info.map((item, index) => {
              return (
                <CarDetailInfo
                  key={index}
                  label={item.label}
                  value={item.value}
                />
              )
            })}
        </Grid>
      </Grid>
    </Flex>
  )
}
